import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/apiClient";
import { ShieldCheck, FileText, Coin, CheckCircle } from "@phosphor-icons/react";

const DECLARATION_PARAGRAPHS = [
  "We hereby state our intention that the USDF assets that we have lawfully purchased and hold be designated as reserved investment assets.",
  "Subject to verification of ownership, applicable laws and regulations, and confirmation of the receiving platform's legal authority and custody arrangements, we intend for the designated USDF holdings to be reserved and invested through Madrefajona-reserved.com.",
  "This declaration reflects our intention concerning assets that are legally under our ownership and control. No transfer, investment, pledge, or custody arrangement shall be considered final until all required documentation, authorization, compliance procedures, and transaction confirmations have been completed.",
];

export default function UsdfReservation() {
  const { t } = useTranslation();
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    holder_name: "",
    holder_email: "",
    usdf_amount: "",
    wallet_reference: "",
    declaration_date: today,
    signature: "",
    note: "",
    acknowledged: false,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null);

  const set = (k) => (e) =>
    setForm((f) => ({
      ...f,
      [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
    }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.acknowledged) {
      setErr("Please acknowledge the declaration to continue.");
      return;
    }
    const amount = parseFloat(String(form.usdf_amount).replace(/[^0-9.]/g, ""));
    if (!amount || amount <= 0) {
      setErr("Please enter a valid USDF amount.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/usdf/declarations", {
        holder_name: form.holder_name,
        holder_email: form.holder_email,
        usdf_amount: amount,
        wallet_reference: form.wallet_reference,
        declaration_date: form.declaration_date,
        signature: form.signature,
        note: form.note || null,
        acknowledged: true,
      });
      toast.success("Declaration received — our compliance desk will reach out.");
      setDone(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e2) {
      const msg = formatApiError(e2.response?.data?.detail) || e2.message;
      setErr(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="usdf-reservation-page" className="text-white">
      {/* Hero */}
      <section className="border-b border-white/10 bg-[#0A0A0A]">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-20 lg:py-24">
          <div className="flex items-center gap-3 mb-5">
            <Coin size={28} weight="duotone" className="text-[#E6C998]" />
            <div className="overline">$USDF · Reserve Stable</div>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mb-6 leading-tight">
            USDF Reservation and
            <br />
            Investment Declaration
            <span className="text-[#E6C998]">.</span>
          </h1>
          <p className="text-neutral-300 max-w-2xl leading-relaxed text-lg">
            Formally designate lawfully-held USDF as a reserved investment asset
            with Madrefajona Reserved. This declaration is an expression of intent
            and does not transfer custody until compliance is complete.
          </p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-5 sm:px-8 py-16 grid grid-cols-1 lg:grid-cols-3 gap-10">
        {/* Declaration body */}
        <aside className="lg:col-span-1 space-y-6">
          <div className="border border-[#E6C998]/20 bg-[#141414] p-6">
            <div className="overline mb-3 flex items-center gap-2">
              <FileText size={14} weight="duotone" className="text-[#E6C998]" />
              Declaration
            </div>
            <div className="space-y-4 text-sm text-neutral-300 leading-relaxed">
              {DECLARATION_PARAGRAPHS.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </div>
          <div className="border border-white/10 bg-black p-6 text-xs text-neutral-500 leading-relaxed">
            <div className="overline mb-2 text-neutral-400">Compliance notice</div>
            Submission does not constitute a transfer of custody. Madrefajona
            Reserved will contact the holder for verification of ownership,
            KYC/AML, and the receiving platform's legal and custody arrangements.
            Read our{" "}
            <Link to="/risk" className="text-[#E6C998] underline">
              risk disclosure
            </Link>{" "}
            and{" "}
            <Link to="/terms" className="text-[#E6C998] underline">
              terms
            </Link>
            .
          </div>
        </aside>

        {/* Form */}
        <div className="lg:col-span-2">
          {done ? (
            <div
              data-testid="usdf-success"
              className="border border-[#E6C998]/40 bg-[#141414] p-8"
            >
              <div className="flex items-center gap-3 mb-4">
                <CheckCircle size={32} weight="duotone" className="text-[#E6C998]" />
                <div>
                  <div className="overline">Reference · {done.id?.slice(-10)}</div>
                  <div className="font-display text-2xl">Declaration received</div>
                </div>
              </div>
              <p className="text-sm text-neutral-300 leading-relaxed mb-6">
                Thank you. Your USDF Reservation & Investment Declaration has
                been logged. Our compliance desk will reach out at{" "}
                <span className="text-[#E6C998]">{form.holder_email}</span> within
                one business day to begin verification of ownership and custody.
              </p>
              <div className="divider-gold my-6" />
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/dashboard"
                  className="btn-gold px-6 py-3 rounded-none text-sm"
                >
                  Go to dashboard
                </Link>
                <Link
                  to="/asset/USDF"
                  className="btn-outline-gold px-6 py-3 rounded-none text-sm"
                >
                  View $USDF
                </Link>
              </div>
            </div>
          ) : (
            <form
              onSubmit={submit}
              className="space-y-6 border border-white/10 bg-[#0A0A0A] p-7 lg:p-9"
            >
              <div className="flex items-center gap-2 mb-2">
                <ShieldCheck size={18} weight="duotone" className="text-[#E6C998]" />
                <div className="overline">Authorized holder details</div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs uppercase tracking-wider text-neutral-400 mb-2">
                    Authorized Holder / Representative
                  </label>
                  <input
                    data-testid="usdf-holder-name"
                    value={form.holder_name}
                    onChange={set("holder_name")}
                    required
                    minLength={2}
                    maxLength={120}
                    placeholder="e.g. James Stugrat"
                    className="w-full bg-black border border-white/20 text-white px-4 py-3 focus:border-[#E6C998] focus:outline-none focus:ring-1 focus:ring-[#E6C998]/40"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-neutral-400 mb-2">
                    Contact email
                  </label>
                  <input
                    data-testid="usdf-holder-email"
                    type="email"
                    value={form.holder_email}
                    onChange={set("holder_email")}
                    required
                    className="w-full bg-black border border-white/20 text-white px-4 py-3 focus:border-[#E6C998] focus:outline-none focus:ring-1 focus:ring-[#E6C998]/40"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs uppercase tracking-wider text-neutral-400 mb-2">
                    USDF Amount (USD)
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#E6C998] text-sm">
                      $
                    </span>
                    <input
                      data-testid="usdf-amount"
                      type="text"
                      inputMode="decimal"
                      value={form.usdf_amount}
                      onChange={set("usdf_amount")}
                      required
                      placeholder="2,000,000"
                      className="w-full bg-black border border-white/20 text-white pl-8 pr-4 py-3 focus:border-[#E6C998] focus:outline-none focus:ring-1 focus:ring-[#E6C998]/40 num"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-neutral-400 mb-2">
                    Declaration date
                  </label>
                  <input
                    data-testid="usdf-date"
                    type="date"
                    value={form.declaration_date}
                    onChange={set("declaration_date")}
                    className="w-full bg-black border border-white/20 text-white px-4 py-3 focus:border-[#E6C998] focus:outline-none focus:ring-1 focus:ring-[#E6C998]/40 num"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wider text-neutral-400 mb-2">
                  Wallet / Account reference
                </label>
                <input
                  data-testid="usdf-wallet"
                  value={form.wallet_reference}
                  onChange={set("wallet_reference")}
                  required
                  minLength={4}
                  maxLength={200}
                  placeholder="0x… or exchange account reference"
                  className="w-full bg-black border border-white/20 text-white px-4 py-3 focus:border-[#E6C998] focus:outline-none focus:ring-1 focus:ring-[#E6C998]/40 font-mono text-sm"
                />
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wider text-neutral-400 mb-2">
                  Signature (typed full legal name)
                </label>
                <input
                  data-testid="usdf-signature"
                  value={form.signature}
                  onChange={set("signature")}
                  required
                  minLength={2}
                  maxLength={200}
                  placeholder="Type your full legal name"
                  className="w-full bg-black border border-[#E6C998]/40 text-[#E6C998] px-4 py-3 focus:border-[#E6C998] focus:outline-none focus:ring-1 focus:ring-[#E6C998]/40 italic text-lg"
                  style={{ fontFamily: "Playfair Display, serif" }}
                />
                <p className="text-xs text-neutral-500 mt-2">
                  Your typed signature constitutes an electronic signature for
                  the purposes of this declaration.
                </p>
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wider text-neutral-400 mb-2">
                  Optional note to compliance
                </label>
                <textarea
                  data-testid="usdf-note"
                  value={form.note}
                  onChange={set("note")}
                  maxLength={2000}
                  rows={3}
                  className="w-full bg-black border border-white/20 text-white px-4 py-3 focus:border-[#E6C998] focus:outline-none focus:ring-1 focus:ring-[#E6C998]/40"
                />
              </div>

              <label
                data-testid="usdf-ack"
                className="flex items-start gap-3 p-4 border border-[#E6C998]/20 bg-[#141414] cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={form.acknowledged}
                  onChange={set("acknowledged")}
                  className="mt-1 accent-[#E6C998]"
                />
                <div className="text-sm text-neutral-300 leading-relaxed">
                  I confirm that I am the lawful owner (or an authorized
                  representative of the owner) of the USDF assets referenced
                  above, and I acknowledge the Declaration shown opposite. I
                  understand that no transfer, pledge or custody is final until
                  all compliance procedures and transaction confirmations have
                  been completed.
                </div>
              </label>

              {err && (
                <div data-testid="usdf-error" className="text-sm text-red-400">
                  {err}
                </div>
              )}

              <button
                type="submit"
                data-testid="usdf-submit"
                disabled={busy}
                className="btn-gold w-full py-4 rounded-none text-sm disabled:opacity-60"
              >
                {busy ? "Submitting declaration…" : "Submit Declaration"}
              </button>

              <p className="text-xs text-neutral-500 leading-relaxed">
                A copy of this declaration will be sent to the compliance desk at
                madrefajona@gmail.com. We may contact you for additional
                verification before any reservation becomes effective.
              </p>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
